import { useTheme } from 'next-themes';
import { Toaster as Sonner, type ToasterProps } from 'sonner';
import { CircleCheckBig, InfoIcon, TriangleAlertIcon, XCircle, Loader2Icon } from 'lucide-react';

const Toaster = ({ ...props }: ToasterProps) => {
    const { theme = 'system' } = useTheme();

    return (
        <Sonner
            theme={theme as ToasterProps['theme']}
            className="toaster group"
            richColors
            closeButton
            icons={{
                success: <CircleCheckBig className="size-4" />,
                info: <InfoIcon className="size-4" />,
                warning: <TriangleAlertIcon className="size-4" />,
                error: <XCircle className="size-4" />,
                loading: <Loader2Icon className="size-4 animate-spin" />,
            }}
            style={
                {
                    '--normal-bg': 'var(--bg-surface)',
                    '--normal-text': 'var(--text-primary)',
                    '--normal-border': 'var(--border-default)',
                    '--border-radius': 'var(--radius-lg)',
                    '--success-bg': 'var(--color-success-50)',
                    '--success-border': 'var(--color-success-200)',
                    '--success-text': 'var(--color-success-700)',
                    '--error-bg': 'var(--color-error-50)',
                    '--error-border': 'var(--color-error-200)',
                    '--error-text': 'var(--color-error-700)',
                    '--warning-bg': 'var(--color-warning-50)',
                    '--warning-border': 'var(--color-warning-200)',
                    '--warning-text': 'var(--color-warning-700)',
                    '--info-bg': 'var(--color-info-50)',
                    '--info-border': 'var(--color-info-200)',
                    '--info-text': 'var(--color-info-700)',
                } as React.CSSProperties
            }
            toastOptions={{
                classNames: {
                    // `pointer-events-auto`: an open modal (the contact drawer, a
                    // dialog) sets `pointer-events: none` on the body, which the
                    // toaster inherits — Undo and every other toast button went
                    // dead exactly when a drawer action raised the toast. Hidden
                    // toasts stay inert: sonner's own `[data-visible=false]` rule
                    // still wins.
                    toast: 'cn-toast pointer-events-auto',
                    // Sonner's own action button (Undo, Go Active, Reload) is a
                    // 24px dark chip in its own colours, so it clashed with every
                    // tinted toast. This is the app's `sm` Button instead,
                    // outlined in the toast's own tone so it reads on every
                    // variant and theme. `!` beats sonner's three-attribute
                    // selector.
                    actionButton:
                        'h-8! rounded-md! border! border-current/30! bg-transparent! px-2.5! text-[0.8125rem]! font-medium! text-current! transition-colors hover:bg-current/10!',
                },
            }}
            {...props}
        />
    );
};

/** The toaster root's attribute — sonner renders it on the `<ol>` list. */
const TOASTER_SELECTOR = '[data-sonner-toaster]';

/**
 * Whether an event came from a toast. A modal layer (the contact drawer, a
 * dialog) reads any press outside its content as "dismiss me"; one open while a
 * toast offers Undo has to let that press through instead of closing — pass
 * this to the layer's `onInteractOutside`.
 */
function isToastTarget(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest(TOASTER_SELECTOR) !== null;
}

export { Toaster, isToastTarget };
